import { useSuspenseQuery } from "@tanstack/react-query";
import {
  buildODataFilter,
  ControlCategories,
  queries,
  useAuthConfig,
  View,
} from "@theprelude/core";
import { useState } from "react";
import { getOdataFilters } from "@/components/filter-form/odata";
import {
  CATEGORY_CONTROL_MAP,
  Category,
  createTopLevelViewName,
  getTopLevelViewName,
  isTopLevelView,
} from "@/components/filter-form/top-level-form";
import { TopLevelFilters } from "@/lib/filter";
import { useCreateView, useDeleteView, useUpdateView } from "./views-mutations";

interface TopLevelViewGroup {
  name: string;
  displayName: string;
  views: Partial<Record<Category, View>>;
}

export function useTopLevelSavedViews() {
  const authConfig = useAuthConfig();

  const savedViewsQuery = useSuspenseQuery({
    ...queries.scm.views(authConfig),
    select: (data) => {
      const topLevelViews = data.filter((view) => isTopLevelView(view.name));

      const grouped = topLevelViews.reduce(
        (acc, view) => {
          const displayName = getTopLevelViewName(view.name);
          if (!acc[displayName]) {
            acc[displayName] = {
              name: view.name,
              displayName,
              views: {},
            };
          }

          const categoryKey = Object.entries(CATEGORY_CONTROL_MAP).find(
            ([_, controlCategory]) => controlCategory === view.category,
          )?.[0] as Category | undefined;

          if (categoryKey) {
            const group = acc[displayName];
            if (group) {
              group.views[categoryKey] = view;
            }
          }
          return acc;
        },
        {} as Record<string, TopLevelViewGroup>,
      );

      return Object.values(grouped);
    },
  });

  const [currentViewGroup, setCurrentViewGroup] = useState<string | null>(null);
  const createView = useCreateView();
  const updateView = useUpdateView();
  const deleteView = useDeleteView();

  // Create views for all categories
  const createTopLevelView = async (name: string, filters: TopLevelFilters) => {
    const viewName = createTopLevelViewName(name);
    const promises: Promise<any>[] = [];

    // Create a view for each category that has filters
    if (filters.endpoints && filters.endpoints.length > 0) {
      promises.push(
        createView.mutateAsync({
          category: ControlCategories.XDR,
          filter: buildODataFilter({
            filter: getOdataFilters(filters.endpoints, false),
          }),
          name: viewName,
        }),
      );
    }

    if (filters.users && filters.users.length > 0) {
      promises.push(
        createView.mutateAsync({
          category: ControlCategories.IDENTITY,
          filter: buildODataFilter({
            filter: getOdataFilters(filters.users, false),
          }),
          name: viewName,
        }),
      );
    }

    if (filters.inboxes && filters.inboxes.length > 0) {
      promises.push(
        createView.mutateAsync({
          category: ControlCategories.EMAIL,
          filter: buildODataFilter({
            filter: getOdataFilters(filters.inboxes, false),
          }),
          name: viewName,
        }),
      );
    }

    await Promise.all(promises);
    setCurrentViewGroup(viewName);
  };

  // Update all views in a group
  const updateTopLevelView = async (
    viewGroup: TopLevelViewGroup,
    filters: TopLevelFilters,
  ) => {
    const promises: Promise<any>[] = [];

    const categories: (keyof TopLevelFilters)[] = [
      "endpoints",
      "users",
      "inboxes",
    ];

    for (const category of categories) {
      const categoryFilters = filters[category];
      const existingView = viewGroup.views[category];

      if (categoryFilters && categoryFilters.length > 0) {
        const filter = buildODataFilter({
          filter: getOdataFilters(categoryFilters, false),
        });

        if (existingView) {
          promises.push(
            updateView.mutateAsync({
              viewId: existingView.id,
              filter,
            }),
          );
        } else {
          promises.push(
            createView.mutateAsync({
              category: CATEGORY_CONTROL_MAP[category],
              filter,
              name: viewGroup.name,
            }),
          );
        }
      } else if (existingView) {
        promises.push(deleteView.mutateAsync(existingView.id));
      }
    }

    await Promise.all(promises);
  };

  // Delete all views in a group
  const deleteTopLevelView = async (viewGroup: TopLevelViewGroup) => {
    const promises = Object.values(viewGroup.views).map((view) =>
      deleteView.mutateAsync(view.id),
    );
    await Promise.all(promises);
    if (currentViewGroup === viewGroup.name) {
      setCurrentViewGroup(null);
    }
  };

  // Update view group name
  const updateTopLevelViewName = async (
    viewGroup: TopLevelViewGroup,
    newName: string,
  ) => {
    const newViewName = createTopLevelViewName(newName);
    const promises = Object.values(viewGroup.views).map((view) =>
      updateView.mutateAsync({
        viewId: view.id,
        name: newViewName,
      }),
    );

    await Promise.all(promises);
    setCurrentViewGroup(newViewName);
  };

  return {
    viewGroups: savedViewsQuery.data || [],
    currentViewGroup,
    setCurrentViewGroup,
    createTopLevelView,
    updateTopLevelView,
    deleteTopLevelView,
    updateTopLevelViewName,
    isPendingSave: createView.isPending || updateView.isPending,
    isPendingDelete: deleteView.isPending,
  };
}
